import { PhoneIcon, PhoneXMarkIcon } from '@heroicons/react/24/outline'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { dialPhoneCall, getCallableContacts, getDialerStatus, getPhoneCall, hangupPhoneCall } from 'services'
import { Input2, Modal } from 'stories/components'
import { setLoanNumber } from 'utils/setLoanNumber'

import { type IPhoneCall, PHONE_CALL_FINAL_STATUSES, PhoneCallStatus, phoneCallStatusLabels } from './types'

interface CallableContact {
  name: string
  phone: string
  role: string
  source: string
}

/** What a finished call should be written up as, handed back to whoever opened this. */
export interface CallPrefill {
  contactName: string
  contactPhone: string
  phoneCallId: number
  direction: string
}

const duration = (seconds: number) => {
  const s = Math.max(0, Number(seconds) || 0)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/**
 * Call a borrower from the app.
 *
 * The company's number rings the collector's own phone first, and connects them to the borrower
 * when they pick up, so what happens here is: choose who to call, watch it connect, then write it
 * up. The write-up is a separate step on purpose — it opens the ordinary call log form, prefilled,
 * rather than inventing a second way to record an outcome that the promise-to-pay sweep would then
 * have to know about.
 *
 * While the call is up this polls rather than holding a socket open. A collections desk makes tens
 * of calls a day, not thousands, and a poll that stops when the modal closes cannot leak a
 * connection that outlives the call.
 */
export const CallModal = ({
  loanNumber,
  contactName = '',
  phone = '',
  onClose,
  onDone,
}: {
  loanNumber: number
  contactName?: string
  phone?: string
  onClose: Function
  onDone: (prefill: CallPrefill) => void
}) => {
  const auth = useSelector((state: any) => state.auth)
  const [loading, setLoading] = useState(true)
  const [dialerStatus, setDialerStatus] = useState<{ canDial: boolean; reason: string; recording: boolean } | null>(
    null,
  )
  const [contacts, setContacts] = useState<CallableContact[]>([])
  const [toNumber, setToNumber] = useState(phone)
  const [toName, setToName] = useState(contactName)
  const [agentPhone, setAgentPhone] = useState(auth?.profile?.phone || '')
  const [call, setCall] = useState<IPhoneCall | null>(null)
  const timer = useRef<any>(null)

  useEffect(() => {
    // The loan-number header is what scopes every call on this screen, and the queue opens this
    // modal for a loan the browser is not sitting on.
    setLoanNumber(loanNumber)
    Promise.all([getDialerStatus(), getCallableContacts().catch(() => [])])
      .then(([status, list]) => {
        setDialerStatus(status)
        setContacts(Array.isArray(list) ? list : [])
        // Nothing was passed in: start on the borrower, which is who a collections call is for.
        if (!phone && Array.isArray(list) && list.length) {
          setToNumber(list[0].phone)
          setToName(list[0].name)
        }
      })
      .catch(() => setDialerStatus({ canDial: false, reason: 'Calling could not be checked.', recording: false }))
      .finally(() => setLoading(false))

    return () => clearInterval(timer.current)
  }, [loanNumber])

  /** Follow the call until the carrier says it is over. */
  const watch = (id: number) => {
    clearInterval(timer.current)
    timer.current = setInterval(async () => {
      try {
        const latest = await getPhoneCall(id)
        setCall(latest)
        if (PHONE_CALL_FINAL_STATUSES.includes(latest?.status)) clearInterval(timer.current)
      } catch {
        clearInterval(timer.current)
      }
    }, 2500)
  }

  const onDial = async () => {
    if (!toNumber) {
      toast.error('Choose a number to call.')
      return
    }
    setLoading(true)
    try {
      const placed = await dialPhoneCall({ toNumber, contactName: toName, agentPhone })
      setCall(placed)
      watch(placed.id)
    } finally {
      setLoading(false)
    }
  }

  const onHangup = async () => {
    if (!call) return
    clearInterval(timer.current)
    setLoading(true)
    try {
      setCall(await hangupPhoneCall(call.id))
    } finally {
      setLoading(false)
    }
  }

  const onLog = () => {
    if (!call) return
    onDone({
      contactName: toName,
      contactPhone: toNumber,
      phoneCallId: call.id,
      direction: 'outbound',
    })
  }

  const isOver = !!call && PHONE_CALL_FINAL_STATUSES.includes(call.status)
  const connected = call?.status === PhoneCallStatus.InProgress

  const renderChoose = () => (
    <div className="w-[30rem] flex flex-col gap-4">
      {contacts.length ? (
        <div>
          <p className="text-[13px] font-semibold text-gray-600 mb-2">Who are you calling?</p>
          <div className="flex flex-col gap-1">
            {contacts.map((c) => (
              <button
                key={`${c.name}-${c.phone}`}
                type="button"
                onClick={() => {
                  setToNumber(c.phone)
                  setToName(c.name)
                }}
                className={`flex justify-between items-center border rounded px-3 py-2 text-left text-sm ${
                  toNumber === c.phone ? 'border-shade-blue bg-shade-blue/5' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <span>
                  <span className="font-semibold">{c.name}</span>
                  {c.role ? <span className="text-[11px] text-gray-500 ml-2 capitalize">{c.role}</span> : null}
                </span>
                <span className="tabular-nums text-gray-600">{c.phone}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <Input2
        title="Number to call"
        value={toNumber}
        type="phone"
        onChange={(value: any) => setToNumber(value)}
        required
      />
      <Input2 title="Spoke with / calling" value={toName} onChange={(value: any) => setToName(value)} />
      <Input2
        title="Ring me on"
        value={agentPhone}
        type="phone"
        tooltip="Your own phone. We call you first, then connect you. Blank uses the number on your user profile."
        onChange={(value: any) => setAgentPhone(value)}
      />
      {dialerStatus?.recording ? (
        <p className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          This call will be recorded. Both you and the person you are calling hear the notice before their phone rings.
        </p>
      ) : null}
    </div>
  )

  const renderLive = () => (
    <div className="w-[30rem] flex flex-col items-center gap-3 py-4 text-center">
      <span
        className={`w-14 h-14 rounded-full flex items-center justify-center ${
          isOver ? 'bg-gray-100 text-gray-500' : 'bg-shade-blue/10 text-shade-blue animate-pulse'
        }`}
      >
        <PhoneIcon className="w-7 h-7" />
      </span>
      <p className="text-lg font-semibold">{phoneCallStatusLabels[call?.status || ''] || 'Calling'}</p>
      <p className="text-sm text-gray-600">
        {toName ? `${toName} · ` : ''}
        {toNumber}
      </p>
      {!isOver && !connected ? (
        <p className="text-[12px] text-gray-500">
          Answer your phone on {call?.agentPhone || agentPhone}. We will connect you as soon as you pick up.
        </p>
      ) : null}
      {isOver ? (
        <p className="text-[13px] text-gray-600">
          {call?.durationSec ? `Lasted ${duration(call.durationSec)}.` : 'The call did not connect.'}{' '}
          {call?.errorMessage}
        </p>
      ) : null}
      {!isOver && (
        <button
          type="button"
          onClick={onHangup}
          className="flex items-center gap-1 text-red-600 border border-red-200 hover:bg-red-50 rounded px-3 py-1.5 text-sm font-semibold"
        >
          <PhoneXMarkIcon className="w-4 h-4" />
          Hang up
        </button>
      )}
    </div>
  )

  const renderBlocked = () => (
    <div className="w-[28rem] text-sm text-gray-700">
      <p>{dialerStatus?.reason}</p>
      <p className="text-[12px] text-gray-500 mt-2">
        Calling is set up under Admin Tools &gt; Configuration &gt; Servicing &gt; Calling.
      </p>
    </div>
  )

  const blocked = !!dialerStatus && !dialerStatus.canDial
  const okayTitle = blocked ? '' : call ? (isOver ? 'Log this call' : '') : 'Call'

  return (
    <Modal
      title={call ? 'Call in progress' : 'Place a call'}
      titleOkay={okayTitle}
      titleCancel={isOver ? 'Skip' : 'Cancel'}
      loading={loading}
      onClose={() => onClose()}
      onOk={call ? onLog : onDial}
      childLevel={2}
      isOpen
    >
      {blocked ? renderBlocked() : call ? renderLive() : renderChoose()}
    </Modal>
  )
}
